// Repeating-entry detail: the template's balanced line set plus its rhythm,
// with a pause action that takes it out of the run without deleting it.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  generateRecurringJournals,
  getRecurringJournalTemplate,
  pauseRecurringJournalTemplate,
  type RecurringJournalTemplate,
} from "@/lib/api";
import { frequencyLabel, useAccountLabels } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "active":
      return "info";
    case "paused":
      return "warning";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function RepeatingEntryDetailPage() {
  const { templateId } = useParams({ strict: false }) as {
    templateId: string;
  };
  const [template, setTemplate] = useState<RecurringJournalTemplate | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const accountLabels = useAccountLabels();

  const reload = useCallback(() => {
    if (!templateId) return;
    setError(null);
    getRecurringJournalTemplate(templateId)
      .then(setTemplate)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [templateId]);

  useEffect(reload, [reload]);

  const pause = async () => {
    if (!templateId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await pauseRecurringJournalTemplate(templateId);
      setNotice(`Paused — this template sits out of the run.`);
      void result;
      setConfirmPause(false);
      reload();
    } catch (cause) {
      setError(`Pause failed: ${toErrorMessage(cause)}`);
      setConfirmPause(false);
    } finally {
      setBusy(false);
    }
  };

  // The generate run is global: it posts every due template across the whole
  // organization, not just the one being viewed.
  const runDue = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await generateRecurringJournals();
      setNotice(
        result.generated_count === 0
          ? "Nothing was due — every template is up to date."
          : `Posted ${result.generated_count} due ${result.generated_count === 1 ? "entry" : "entries"}.`,
      );
      setConfirmRun(false);
      reload();
    } catch (cause) {
      setError(`Run failed: ${toErrorMessage(cause)}`);
      setConfirmRun(false);
    } finally {
      setRunning(false);
    }
  };

  const canPause =
    template !== null && template.status.toLowerCase() === "active";

  const totals = template
    ? template.lines.reduce(
        (sum, line) => ({
          debit: sum.debit + line.debit_minor,
          credit: sum.credit + line.credit_minor,
        }),
        { debit: 0, credit: 0 },
      )
    : { debit: 0, credit: 0 };

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/recurring-journals"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Repeating entries
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {template?.name ?? templateId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {template === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : template ? (
        <>
          <PageHeader
            title={template.name}
            description={
              <>
                {frequencyLabel(template.frequency)} ·{" "}
                {template.occurrences_generated}{" "}
                {template.occurrences_generated === 1 ? "posting" : "postings"}{" "}
                so far ·{" "}
                <StatusDot
                  tone={statusTone(template.status)}
                  label={plainStatus(template.status)}
                />
              </>
            }
            actions={
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirmRun(true)}
                  disabled={busy || running}
                  title="Post every repeating entry due today across the whole organization — not just this template"
                >
                  Run all due templates
                </Button>
                {canPause ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirmPause(true)}
                    disabled={busy || running}
                    title="Take this template out of the run without deleting it"
                  >
                    Pause
                  </Button>
                ) : null}
              </>
            }
          />

          {template.notes ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {template.notes}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Starts
              </p>
              <p className="mt-1 text-sm">
                {new Date(template.start_date).toLocaleDateString()}
                {template.end_date
                  ? ` → ${new Date(template.end_date).toLocaleDateString()}`
                  : " · no end date"}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Next run
              </p>
              <p className="mt-1 text-sm">
                {template.next_run_date
                  ? new Date(template.next_run_date).toLocaleDateString()
                  : "—"}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Last run
              </p>
              <p className="mt-1 text-sm">
                {template.last_run_date
                  ? new Date(template.last_run_date).toLocaleDateString()
                  : "Never run"}
              </p>
            </div>
          </div>

          <section aria-label="Template lines" className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              What each posting writes
            </h2>
            <div className="surface overflow-hidden rounded-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">In</TableHead>
                    <TableHead className="text-right">Out</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {template.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        This template has no lines yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    template.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell title={line.account_id}>
                          {accountLabels.get(line.account_id) ??
                            "Unknown account"}
                        </TableCell>
                        <TableCell className="text-xs text-[var(--muted-foreground)]">
                          {line.description}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {line.debit_minor > 0
                            ? formatMoney({
                                cents: line.debit_minor,
                                currency: "USD",
                              })
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {line.credit_minor > 0
                            ? formatMoney({
                                cents: line.credit_minor,
                                currency: "USD",
                              })
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <p
              className="text-xs text-[var(--muted-foreground)]"
              title="Debits equal credits (balanced recurring-journal template)"
            >
              Balanced: {formatMoney({ cents: totals.debit, currency: "USD" })}{" "}
              in = {formatMoney({ cents: totals.credit, currency: "USD" })} out.
            </p>
          </section>
        </>
      ) : null}

      <Dialog
        open={confirmRun}
        onOpenChange={(open) => {
          if (!open && !running) setConfirmRun(false);
        }}
        title="Run all due templates?"
        description="Posts a journal for every repeating entry due as of today — across the whole organization, not just this one."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRun(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void runDue()}
            disabled={running}
          >
            {running ? "Running…" : "Run all due templates"}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={confirmPause}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirmPause(false);
        }}
        title="Pause this template?"
        description="It stays saved and its lines are kept, but it sits out of the run until it is resumed."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmPause(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void pause()} disabled={busy}>
            {busy ? "Pausing…" : "Pause template"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
